/* eslint-disable @typescript-eslint/no-explicit-any */
import useUpdateAgent from '@/hooks/useUpdateAgent';
import { formatColumnName } from '@/utils/formatColumnName';
import { Edit3 } from 'lucide-react';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import SelectForm from './ui/SelectForm';

export interface SelectFieldConfig {
  label: string;
  placeholder: string;
  fieldKey: string;
  items?: Array<{ label: string; value: string }>;
  defaultValue?: string;
}

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  record: any;
  columns: string[];
  excludeColumns?: string[];
  selectField?: SelectFieldConfig;
  successMessage?: string;
}

const EditRecordModal: FC<Props> = ({
  record,
  isOpen,
  setIsOpen,
  columns,
  excludeColumns = ['inviteSent', 'registeredAt'],
  selectField,
  successMessage = 'Successfully Edited Record',
}) => {
  const { mutate: updateAgent, isPending: updatePending } = useUpdateAgent();
  const [editingRow, setEditingRow] = useState(record);

  const editableFields = columns.filter((col) => !excludeColumns.includes(col));

  const handleFieldChange = (field: string, value: string) => {
    setEditingRow((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    updateAgent(
      { id: editingRow._id, ...editingRow },
      {
        onSuccess: () => {
          toast.success(successMessage);
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Record
          </DialogTitle>
        </DialogHeader>

        {record && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {editableFields.map((field) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={field} className="text-sm font-medium">
                    {formatColumnName(field)}
                  </Label>
                  <Input
                    id={field}
                    value={editingRow[field] || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={`Enter ${formatColumnName(field).toLowerCase()}`}
                  />
                </div>
              ))}
              {selectField && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {selectField.label}
                  </Label>
                  <SelectForm
                    placeholder={selectField.placeholder}
                    items={selectField.items}
                    defaultValue={selectField.defaultValue || ''}
                    onChange={(val) =>
                      handleFieldChange(selectField.fieldKey, val)
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            disabled={updatePending}
            variant="outline"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updatePending}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRecordModal;
